import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ButtonDropdown, Table, Tag } from '@bitcine/cinesend-theme'
import useStore from '../../hooks/use_store'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import useTableFilters from '../../hooks/use_table_filters'
import Tabs from '../layouts/tabs'
import { convertToShorthand } from '../../helpers/convert_date'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'

const GroupsPoolsTable = ({ queryKey }) => {
  const params = useParams()
  const { deleteModel } = useStore()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: airline, error: airlineError } = useQuery(['airlines/', params.airlineID])
  const langID = airline?.languages?.[0]?.id || null

  const { queryString, searching, sorting, filtering } = useTableFilters({
    filteringOptions: null,
    enableSearch: true,
    queryExists: true,
    persistKey: `groups-pools-table-${params.cycleID}`,
  })

  const selectedTab = searchParams.get('type') ?? 'warning-slates'
  const isAdPool = selectedTab === 'ad-pools'
  const widths = isAdPool ? [100, 400, 100, 100, 100, 65] : [100, 600, 100, 65]
  const queryWithTypeParam = `${queryString}&type=${selectedTab}`

  const { data: categories, isLoading, error } = useQuery([queryKey, queryWithTypeParam])

  const navigate = useNavigate()

  const typeToNameMapping = {
    'warning-slates': 'Warning Slate Groups',
    'ad-pools': 'Ad Pools',
  }

  return (
    <>
      <Tabs
        className='mb-4'
        list={['warning-slates', 'ad-pools'].map((type) => {
          return {
            name: typeToNameMapping[type],
            selected: selectedTab === type,
            onClick: () => setSearchParams({ type }),
          }
        })}
      />
      <Table
        key={selectedTab}
        status={{
          pending: isLoading || !airline,
          error: error || airlineError,
          errorMessage: error?.message || airlineError?.message,
        }}
        widths={widths}
        header={{
          columns: [
            { text: 'Title', key: 'title' },
            { text: 'Assigned Assets', key: 'assigned_assets' },
            { text: 'Count', key: 'count' },
            ...(isAdPool
              ? [
                  { text: 'Start Date', key: 'start_date' },
                  { text: 'End Date', key: 'end_date' },
                ]
              : []),
            { text: '' },
          ],
          searching,
          sorting,
          filtering,
        }}
        body={{
          data: categories,
          row: {
            compact: true,
            spaced: true,
            langID: langID,
            onClick: (event, data) =>
              navigate(`/airlines/${params.airlineID}/cycles/${params.cycleID}/ads/groups-pools/${data.id}/items`),
            render: [
              (data) =>
                selectedTab === 'ad-pools'
                  ? data.external_unique_name || MISSING_UNIQUE_NAME
                  : data.default_title || '',
              (data) => (
                <div className='space-x-2'>
                  {(data.assigned_assets ?? []).map((item, index) => (
                    <Tag key={`${data.id}-${item?.title}-${index}`} outline label={item?.title} />
                  ))}
                </div>
              ),
              (data) => (data.assigned_assets ?? []).length,
              ...(isAdPool
                ? [(data) => convertToShorthand(data.start_date), (data) => convertToShorthand(data.end_date)]
                : []),
              (data) => (
                <ButtonDropdown
                  kebab
                  dropdown={{
                    content: [
                      {
                        text: 'Delete',
                        className: 'text-red-500',
                        onClick: () =>
                          deleteModel.mutate(
                            {
                              endpoint: 'categories',
                              id: data.id,
                            },
                            {
                              onSuccess: () => {
                                queryClient.invalidateQueries([queryKey])
                              },
                            }
                          ),
                      },
                    ],
                  }}
                />
              ),
            ],
          },
          empty: {
            title: `No ${typeToNameMapping[selectedTab]} found!`,
            text: `Create a new ${typeToNameMapping[selectedTab]} to begin.`,
            icon: 'category',
          },
        }}
      />
    </>
  )
}

export default GroupsPoolsTable
